import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import axios from 'axios';

import TopSection from "../../LT/TopSection";
import TabSection from "../../../components/common/TabSection";
import PriceMemoData from "./PriceMemoData";
import { Loader } from "../../../components/common/Loader";
import ButtonComponent from "../../../components/common/button/Button";
import ContractData from "./ContractData";
import { getContractTrackingReportEndpoint, getReports, reOpenContractEndpoint } from "../../../common/endpoints";
import { fetchPriceMemoReportData } from "../../../redux/actions/PriceMemoReport";
import { fetchContractReportData } from "../../../redux/actions/ContractReport";
import Cards from "../../../components/common/card/Card";
import { reOpenContractApi } from "../../../redux/actions/ContractReport/ReOpen";
import { toast } from "react-toastify";
import { setSelectedItem } from "../../../redux/actions/SupportTicket/selectionSlice";
import { setTabName } from "../../../redux/actions/SupportTicket/tabSlice";

export const Reporting = () => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [pageSize, setPageSize] = useState(3500);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPageItems, setTotalPageItems] = useState(0);
  const [totalItemCurrentPage, setTotalItemCurrentPage] = useState(0);
  const [contractPageSize, setContractPageSize] = useState(3500);
  const [contractCurrentPage, setContractCurrentPage] = useState(1);
  const [contractTotalPages, setContractTotalPages] = useState(0);
  const [contractTotalPageItems, setContractTotalPageItems] = useState(0);
  const [contractTotalItemCurrentPage, setContractTotalItemCurrentPage] = useState(0);
  const [rowsData, setRowsData] = useState([]);
  const [btnDisabled, setIsBtnDisabled] = useState(true);
  const [seqIds, setSeqIds] = useState([]);
  const [seqContractIds, setSeqContractIds] = useState([]);
  const [historySeqIds, setHistorySeqIds] = useState([]);
  const [disableReOpen, setDisableReOpen] = useState(true);
  const procurementDivisionType = useSelector((state) => state?.userCardType?.procurementDivisionType);
  const piceMemoDataList = useSelector((state) => state?.fetchPriceMemoReports);
  const contractDataList = useSelector((state) => state?.fetchContractReports);
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);

  const cdRole = localStorage.getItem("CDRole");

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    if (newValue === 0) {
      dispatch(setTabName("Price Memo"))
      fetchPMDataFromApi();
      setIsBtnDisabled(true);
    } else if (newValue === 1) {
      dispatch(setTabName("Contracts"))
      fetchContractsDataFromApi();
      setIsBtnDisabled(true);
    }
  };

  const onSelectionChanged = (params) => {
    const displayedRows = [];
    params?.api?.forEachNodeAfterFilterAndSort((node) => {
      if (node.isSelected()) {
        displayedRows.push(node.data);
      }
    });
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const rowsInCurrentPage = displayedRows.slice(startIndex, endIndex);
  
    const selectedRows = rowsInCurrentPage.filter((row) =>
      params.api.getSelectedNodes().some((node) => node.data === row)
    );

    setRowsData(selectedRows);
    dispatch(setSelectedItem(selectedRows));

    const ids = selectedRows?.map((item) => item?.runOutputSeqId);
    setSeqIds(ids);
    const historyids = selectedRows.map((item) => item?.contractHistorySeqId);
    setHistorySeqIds(historyids)
    const conIds = selectedRows.map((item) => item?.contractSeqId);
    setSeqContractIds(conIds);
    const reOpen = selectedRows?.length > 0 &&  selectedRows?.every((item) => item?.status === "CONTRACT CREATION SUCCESSFUL" || item?.status === "CONTRACT UPDATE SUCCESSFUL");
    setDisableReOpen(!reOpen);
    setIsBtnDisabled(selectedRows.length === 0);
  };

  const rowSelection = useMemo(() => {
    return {
      mode: "multiRow",
      isRowSelectable: (rowNode) => (rowNode.data ? true : false),
    };
  }, []);

  const onPageSizeChanged = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1); // Reset to first page
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const onContractPageSizeChanged = (event) => {
    setContractPageSize(Number(event.target.value));
    setContractCurrentPage(1); // Reset to first page
  };

  const goToPageContract = (page) => {
    if (page >= 1 && page <= totalPages) {
      setContractCurrentPage(page);
    }
  };

  const fetchPMDataFromApi = useCallback( async () => {
    try {
      const response = await dispatch(
        fetchPriceMemoReportData({
          dynamicEndpoint: `${getReports}/${procurementDivisionType}/priceMemo`,
          params: {
            page: currentPage,
            size: pageSize,
            plantId: localStorage.getItem("selectedCDPlantCode"),
          },
        })
      );
      if (response?.meta?.requestStatus === "fulfilled") {
        setTotalPages(response?.payload?.totalPages);
        setTotalPageItems(response?.payload?.totalNumberOfItems);
        setTotalItemCurrentPage(response?.payload?.totalItemsInPage);
      } else {
        console.log("Failed to fetch data:", response.payload.responseMessage);
      }
    } catch (error) {
      console.error("API error:", error);
    }
  }, [dispatch, procurementDivisionType, currentPage, pageSize]);

  const fetchContractsDataFromApi = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchContractReportData({
          dynamicEndpoint: `${getReports}/${procurementDivisionType}/contracts`,
          params: {
            page: contractCurrentPage,
            size: contractPageSize,
            plantId: localStorage.getItem("selectedCDPlantCode"),
          },
        })
      );
      if (response.meta.requestStatus === "fulfilled") {
        setContractTotalPages(response?.payload?.totalPages);
        setContractTotalPageItems(response?.payload?.totalNumberOfItems);
        setContractTotalItemCurrentPage(response?.payload?.totalItemsInPage);
      } else {
        console.log("Failed to fetch data:", response.payload.responseMessage);
      }
    } catch (error) {
      console.log("API error:", error);
    }
  }, [
    dispatch,
    procurementDivisionType,
    contractCurrentPage,
    contractPageSize,
  ]);
  
  useEffect(() => {
    if(tab === 0) {
      fetchPMDataFromApi();
    } else {
      fetchContractsDataFromApi();
    }
  }, [fetchPMDataFromApi, fetchContractsDataFromApi, procurementDivisionType, tab]);

  const downloadPMHandler = async (event) => {
    event.preventDefault();
    const account = instance?.getAllAccounts()[0]; // get the first account if there are multiple
    instance?.setActiveAccount(account);
    const tokenRequest = {
        scopes: [process.env.REACT_APP_CLIENT_ID + "/.default"], // You can specify additional scopes
    };
    const response = await instance?.acquireTokenSilent(tokenRequest);
    const accessToken = response?.accessToken;

    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/${getReports}/${procurementDivisionType}/price-memo/download`;
    const requestBody = {
      seqIds: seqIds,
    };
    try {
      const response = await axios.post(url, requestBody, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          withCredentials: true,
        },
        params: {
          plantId: localStorage.getItem("selectedCDPlantCode"),
          userEmail: localStorage.getItem("data"),
          LTRole: localStorage.getItem("LTRole"),
          CDRole: localStorage.getItem("CDRole")
        },
      });
      
      if(response.status === 200) {
        const csvContent = `data:text/csv;charset=utf-8,${response?.data}\n`;
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `${procurementDivisionType}-price-memo-report.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        fetchPMDataFromApi();
        setIsBtnDisabled(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }

  const downloadContractHandler = async (event) => {
    event.preventDefault();
    const account = instance?.getAllAccounts()[0]; // get the first account if there are multiple
    instance?.setActiveAccount(account);
    const tokenRequest = {
        scopes: [process.env.REACT_APP_CLIENT_ID + "/.default"], // You can specify additional scopes
    };
    const response = await instance?.acquireTokenSilent(tokenRequest);
    const accessToken = response?.accessToken;

    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/${getReports}/${procurementDivisionType}/contract/download`;
    const requestBody = {
      seqIds: historySeqIds,
    };
    try {
      const response = await axios.post(url, requestBody, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          withCredentials: true,
        },
        params: {
          plantId: localStorage.getItem("selectedCDPlantCode"),
          userEmail: localStorage.getItem("data"),
          LTRole: localStorage.getItem("LTRole"),
          CDRole: localStorage.getItem("CDRole")
        },
      });
      
      if(response.status === 200) {
        const csvContent = `data:text/csv;charset=utf-8,${response?.data}\n`;
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `${procurementDivisionType}-contract-report.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        fetchContractsDataFromApi();
        setIsBtnDisabled(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }

  const downloadContractTrackingReport = async (event) => {
    event.preventDefault();
    setLoading(true);
    const account = instance?.getAllAccounts()[0];
    instance?.setActiveAccount(account);
    const tokenRequest = {
      scopes: [process.env.REACT_APP_CLIENT_ID + "/.default"],
    };
    try {
      const tokenResponse = await instance?.acquireTokenSilent(tokenRequest);
      const accessToken = tokenResponse?.accessToken;

      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/${getContractTrackingReportEndpoint}`;
      const response = await axios.get(
        url,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            withCredentials: true,
          },
          params: {
            plantId: localStorage.getItem("selectedCDPlantCode"),
          },
          responseType: "blob", // Important for Excel download
        }
      );

      if (response.status === 200) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute(
          "download",
          `contract-tracking-report.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        setLoading(false);
        fetchContractsDataFromApi();
        fetchPMDataFromApi();
      }
    } catch (error) {
      console.error("Error downloading contract tracking report:", error);
    }
  };

  const reOpenHandler = async () => {
    try {
      const response = await dispatch(
        reOpenContractApi({
          dynamicEndpoint: `${reOpenContractEndpoint}`,
          params: {
            plantId: localStorage.getItem("selectedCDPlantCode"),
          },
          data: {
            contractSeqIds: seqContractIds,
          },
        })
      );
      if (response.meta.requestStatus === "fulfilled") {
        toast.success("Contracts Re-Opened Successfully", {
          autoClose: 3000,
        });
        fetchContractsDataFromApi();
        setIsBtnDisabled(true);
        setDisableReOpen(true);
      } else {
        toast.error("Something went wrong, Please try again", {
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Something went wrong, Please try again", {
        autoClose: 3000,
      });
    }
  }

  return (
    <>
      <TopSection
        labelList={[
          { title: "Home", path: "/landing-page" },
          { title: "Reporting" },
        ]}
        hideHeader={true}
        headerName={"Contract Data Reporting"}
      />
      {piceMemoDataList?.loading === true || contractDataList?.loading === true || loading === true ?  (
        <Loader />
      ) : (
        <>
          {tab === 0 ? (
            <div className="cards-container">
              <Cards
                cards={[
                  {
                    title: "Total Price Memo",
                    value: piceMemoDataList?.data?.counts?.totalRecordCount || 0,
                    description: "Total Number of Records in the Price Memo",
                  },
                  {
                    title: "Submitted Records",
                    value: piceMemoDataList?.data?.counts?.submittedForContractsCount || 0,
                    description: "Total number of Records which are Successfully Submitted",
                  },
                  {
                    title: "Failed Records",
                    value: piceMemoDataList?.data?.counts?.systemRejectedRecordCount + piceMemoDataList?.data?.counts?.userRejectedRecordCount || 0,
                    description: "Total number of Records which are failed",
                  },
                ]}
              />
            </div>
          ) : (
            <div className="cards-container">
              <Cards
                cards={[
                  {
                    title: "Total Contracts",
                    value: contractDataList?.data?.contractDataCount?.totalContractsCount || 0,
                    description: "Total Number of Records in the Contracts",
                  },
                  {
                    title: "Success Records",
                    value: contractDataList?.data?.contractDataCount?.successfulContractsCount || 0,
                    description:
                      "Total number of Records which are Successfully Submitted",
                  },
                  {
                    title: "Failed Records",
                    value: contractDataList?.data?.contractDataCount?.failedContractsCount || 0,
                    description: "Total number of Records which are failed",
                  },
                ]}
              />
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", margin: "20px 0 20px 0" }}>
            <ButtonComponent
              label="Download Contracts Tracking Report"
              bgColor="#000"
              color="#fff"
              maxWidth="300px"
              onClick={downloadContractTrackingReport}
            />
          </div>
          <div className="tab-part" style={{border: "1px solid #ccc", borderRadius: "8px"}}>
            <TabSection
              style={{
                maxWidth: "450px",
                marginBottom: "10px",
                marginTop: "10px",
                width: "100%",
              }}
              label={["Price Memo", "Contracts"]}
              value={tab}
              handleChange={handleTabChange}
            />
            {tab === 0 ? (
                <div className="priceMemo_dataTable">
                  <PriceMemoData
                    data={piceMemoDataList?.data}
                    onPageSizeChanged={onPageSizeChanged}
                    goToPage={goToPage}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalPageItems={totalPageItems}
                    totalItemCurrentPage={totalItemCurrentPage}
                    onSelectionChanged={onSelectionChanged}
                    rowsData={rowsData}
                    rowSelection={rowSelection}
                  />
                  <div
                    style={{
                      marginTop: "50px",
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "30px",
                    }}
                  >
                    <div></div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <ButtonComponent
                        label="Download"
                        bgColor="#000"
                        color="#fff"
                        disabled={btnDisabled}
                        onClick={downloadPMHandler}
                      />
                    </div>
                  </div>
                </div>
            ) : (
              <div className="priceMemo_dataTable">
                <ContractData
                  data={contractDataList?.data}
                  onContractPageSizeChanged={onContractPageSizeChanged}
                  goToPageContract={goToPageContract}
                  pageSize={contractPageSize}
                  totalPages={contractTotalPages}
                  currentPage={contractCurrentPage}
                  totalPageItems={contractTotalPageItems}
                  totalItemCurrentPage={contractTotalItemCurrentPage}
                  onSelectionChanged={onSelectionChanged}
                  rowsData={rowsData}
                  rowSelection={rowSelection}
                />
                <div
                  style={{
                    marginTop: "50px",
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "30px",
                  }}
                >
                  <div></div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: `${cdRole === "Viewer" ? "100px" : "200px"}`,
                    }}
                  >
                  { cdRole === "Viewer" ? null :
                    <ButtonComponent
                      label="Re-Open"
                      bgColor="#000"
                      color="#fff"
                      disabled={disableReOpen}
                      onClick={reOpenHandler}
                    />
                  }
                    <ButtonComponent
                      label="Download"
                      bgColor="#000"
                      color="#fff"
                      disabled={btnDisabled}
                      onClick={downloadContractHandler}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
